<script setup lang="ts">
/**
 * Список шагов проверки с текущим статусом.
 *
 * Отдельный компонент нужен, потому что проверка идёт ощутимое время: загрузка
 * заблокированных ресурсов ждёт таймаута, и без видимого прогресса страница
 * выглядела бы зависшей.
 */

import type { StepState } from '../checks/runner.js';

defineProps<{ steps: StepState[] }>();

const MARKS: Record<StepState['status'], string> = {
  pending: '·',
  running: '…',
  done: '✓',
  failed: '×',
};
</script>

<template>
  <section class="card" aria-live="polite">
    <h2>Идёт проверка</h2>
    <ul class="steps">
      <li v-for="step in steps" :key="step.id" :class="['step', step.status]">
        <span class="mark" aria-hidden="true">{{ MARKS[step.status] }}</span>
        <span class="body">
          <span class="title">{{ step.title }}</span>
          <span v-if="step.note" class="note">{{ step.note }}</span>
        </span>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.card {
  margin-top: 24px;
  padding: 20px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
}

h2 {
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
}

.steps {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.step {
  display: flex;
  gap: 12px;
  align-items: baseline;
  color: var(--text-muted);
}

.step.running,
.step.done {
  color: var(--text);
}

.mark {
  width: 16px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.step.done .mark {
  color: var(--severity-none);
}

.step.failed .mark,
.step.failed {
  color: var(--severity-high);
}

.body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.note {
  color: var(--text-muted);
  font-size: 14px;
}
</style>
